import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Crown, AlertTriangle } from 'lucide-react';
import { useCurrentDojo } from '@/hooks/useCurrentDojo';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

const TrialCountdownTimer: React.FC = () => {
  useUiLanguage();
  const { dojo, loading } = useCurrentDojo();
  const { t } = useTranslation('common');
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!dojo || loading || dojo.name === 'Taiga Dojo' || dojo.plan !== 'trial' || !dojo.trial_start) {
      setTimeLeft(null);
      setIsExpired(false);
      return;
    }

    const calculateTimeLeft = () => {
      const trialStart = new Date(dojo.trial_start!);
      const trialEnd = new Date(trialStart.getTime() + (14 * 24 * 60 * 60 * 1000)); // 14 days
      const now = new Date();
      const difference = trialEnd.getTime() - now.getTime();

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({
        days,
        hours,
        minutes,
        seconds,
        totalMs: difference
      });
      setIsExpired(false);
    };

    // Calculate initial time
    calculateTimeLeft();

    // Update every second
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [dojo, loading]);

  // Don't show for non-trial accounts
  if (loading || !dojo || dojo.name === 'Taiga Dojo' || dojo.plan !== 'trial' || !dojo.trial_start) {
    return null;
  }

  // Show expired state
  if (isExpired) {
    return (
      <Card className="border-red-200 bg-red-50/50 animate-fade-in">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            {t("common:trial.expired")}
          </CardTitle>
          <CardDescription className="text-red-600">
            {t("common:trial.expiredMessage")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/subscription">
            <Button className="w-full bg-red-600 hover:bg-red-700">
              <Crown className="h-4 w-4 mr-2" />
              {uiText("Upgrade to Pro ($49.99/month)")}</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  // Show countdown
  if (!timeLeft) {
    return null;
  }

  const isUrgent = timeLeft.days <= 2;
  const isWarning = timeLeft.days <= 5;

  return (
    <Card className={`animate-fade-in transition-all duration-300 ${
      isUrgent
        ? 'border-red-200 bg-red-50/50 shadow-lg'
        : isWarning
        ? 'border-orange-200 bg-orange-50/50'
        : 'border-blue-200 bg-blue-50/50'
    }`}>
      <CardHeader className="pb-3">
        <CardTitle className={`flex items-center gap-2 ${
          isUrgent
            ? 'text-red-700'
            : isWarning
            ? 'text-orange-700'
            : 'text-blue-700'
        }`}>
          <Clock className="h-5 w-5" />
          {t("common:trial.countdown")}
        </CardTitle>
        <CardDescription className={
          isUrgent
            ? 'text-red-600'
            : isWarning
            ? 'text-orange-600'
            : 'text-blue-600'
        }>
          {t("common:trial.expiresSoon")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Countdown Display */}
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              isUrgent
                ? 'text-red-700'
                : isWarning
                ? 'text-orange-700'
                : 'text-blue-700'
            }`}>
              {timeLeft.days}
            </div>
            <div className="text-xs text-muted-foreground">{t("common:trial.days")}</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              isUrgent
                ? 'text-red-700'
                : isWarning
                ? 'text-orange-700'
                : 'text-blue-700'
            }`}>
              {timeLeft.hours}
            </div>
            <div className="text-xs text-muted-foreground">{t("common:trial.hours")}</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              isUrgent
                ? 'text-red-700'
                : isWarning
                ? 'text-orange-700'
                : 'text-blue-700'
            }`}>
              {timeLeft.minutes}
            </div>
            <div className="text-xs text-muted-foreground">{t("common:trial.minutes")}</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              isUrgent
                ? 'text-red-700'
                : isWarning
                ? 'text-orange-700'
                : 'text-blue-700'
            }`}>
              {timeLeft.seconds}
            </div>
            <div className="text-xs text-muted-foreground">{t("common:trial.seconds")}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{t("common:trial.progress")}</span>
            <span>{Math.round(((14 * 24 * 60 * 60 * 1000 - timeLeft.totalMs) / (14 * 24 * 60 * 60 * 1000)) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-1000 ${
                isUrgent
                  ? 'bg-gradient-to-r from-red-500 to-red-600'
                  : isWarning
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600'
              }`}
              style={{
                width: `${Math.min(100, ((14 * 24 * 60 * 60 * 1000 - timeLeft.totalMs) / (14 * 24 * 60 * 60 * 1000)) * 100)}%`
              }}
            />
          </div>
        </div>

        {/* Upgrade Button */}
        <Link to="/subscription">
          <Button
            className={`w-full ${
              isUrgent
                ? 'bg-red-600 hover:bg-red-700'
                : isWarning
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } ${isUrgent ? 'animate-pulse' : ''}`}
          >
            <Crown className="h-4 w-4 mr-2" />
            {t("common:trial.upgradeNow")}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default TrialCountdownTimer;